import { supabase } from './supabase.js';
import { profileGroovesBalance } from '../marketplace/constants/groovesEconomy.js';
import { compressImage } from './storageService.js';

export const SORTEO_BUCKET = 'sorteos';

/** Días que un sorteo ya celebrado sigue en el tablón de Comunidad antes de ocultarse. */
export const SORTEO_TABLON_DIAS_TRAS_FIN = 6;

const MS_DIA = 86_400_000;

/** Fecha de sorteo pasada o estado marcado como realizado en admin. */
export function sorteoCelebrado(row) {
  if (!row) return false;
  if (row.estado === 'realizado') return true;
  const fs = row.fecha_sorteo ? new Date(row.fecha_sorteo).getTime() : NaN;
  return Number.isFinite(fs) && Date.now() >= fs;
}

/** Visible en Comunidad: publicados activos o terminados dentro de la ventana de archivo. */
export function sorteoVisibleEnTablonComunidad(row) {
  if (!row || row.estado === 'borrador') return false;
  if (!sorteoCelebrado(row)) return row.estado === 'publicado' || row.estado === 'cerrado';
  const fs = row.fecha_sorteo ? new Date(row.fecha_sorteo).getTime() : NaN;
  if (!Number.isFinite(fs)) return true;
  return Date.now() < fs + SORTEO_TABLON_DIAS_TRAS_FIN * MS_DIA;
}

/** Sorteos visibles público (excepto borradores por RLS). */
export async function fetchSorteosComunidad() {
  const { data, error } = await supabase
    .from('sorteos')
    .select(
      [
        'id',
        'titulo',
        'descripcion',
        'producto',
        'imagen_url',
        'video_url',
        'resultado_video_url',
        'fecha_sorteo',
        'inscripciones_desde',
        'inscripciones_hasta',
        'modalidad_precio',
        'grooves_entrada',
        'estado',
        'ganadores_previstos',
        'legal_texto',
        'legal_url',
        'inscripciones_count',
        'ganador_principal_user_id',
        'ganador_suplente_2_user_id',
        'ganador_suplente_3_user_id',
      ].join(', ')
    )
    .order('fecha_sorteo', { ascending: false });

  if (error) {
    console.warn('[sorteosService] fetch:', error.message);
    return [];
  }
  const list = (data || []).filter(sorteoVisibleEnTablonComunidad);
  return attachSorteosGanadoresNombres(list);
}

/** Añade nombres de usuario para línea oficial ganador principal + suplentes. */
async function attachSorteosGanadoresNombres(rows) {
  if (!rows.length) return rows;
  const ids = [
    ...new Set(
      rows.flatMap(r => [r.ganador_principal_user_id, r.ganador_suplente_2_user_id, r.ganador_suplente_3_user_id]).filter(Boolean)
    ),
  ];
  if (!ids.length) return rows;
  const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
  const nm = Object.fromEntries((profs || []).map(p => [p.id, p.username || null]));
  return rows.map(r => ({
    ...r,
    _ganador_principal_username: nm[r.ganador_principal_user_id] || null,
    _ganador_suplente_2_username: nm[r.ganador_suplente_2_user_id] || null,
    _ganador_suplente_3_username: nm[r.ganador_suplente_3_user_id] || null,
  }));
}

export async function fetchMisSorteosInscripciones(userId) {
  if (!userId) return [];
  const { data, error } = await supabase.from('sorteos_inscripciones').select('sorteo_id').eq('user_id', userId);
  if (error) return [];
  return (data || []).map(r => r.sorteo_id);
}

export async function inscribirSorteoRpc(sorteoId) {
  const { data, error } = await supabase.rpc('inscribir_sorteo', { p_sorteo_id: sorteoId });
  if (error) return { ok: false, code: error.message };
  let row = data;
  if (typeof row === 'string') {
    try {
      row = JSON.parse(row);
    } catch {
      row = {};
    }
  }
  if (typeof row !== 'object' || !row) row = {};
  return { ok: !!row.ok, code: row.code || null, groovesPaid: row.grooves_paid ?? 0 };
}

export async function fetchProfileGrooves(userId) {
  if (!userId) return { balance: null, phoneVerified: false };
  const { data } = await supabase
    .from('profiles')
    .select('grooves, grooves_total, phone_verified_at')
    .eq('id', userId)
    .maybeSingle();
  if (!data) return { balance: null, phoneVerified: false };
  const g = profileGroovesBalance(data);
  return {
    balance: Number.isFinite(g) ? g : 0,
    phoneVerified: !!data.phone_verified_at,
  };
}

/**
 * Panel admin: subida imagen al bucket público sorteos/.
 * @param {File} file
 * @param {string} sorteoId UUID
 */
export async function uploadSorteoImagen(file, sorteoId) {
  if (!file || !sorteoId) return { url: null, error: 'missing' };
  const compressed = await compressImage(file);
  const rawExt =
    (compressed.name && compressed.name.includes('.') ? compressed.name.split('.').pop() : '') || 'jpg';
  const ext = /^[a-z0-9]+$/i.test(rawExt) ? rawExt.toLowerCase() : 'jpg';
  const objectPath = `${sorteoId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from(SORTEO_BUCKET).upload(objectPath, compressed, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) return { url: null, error: error.message };
  const { data } = supabase.storage.from(SORTEO_BUCKET).getPublicUrl(objectPath);
  return { url: data?.publicUrl || null, error: null };
}

export function youtubeEmbedUrl(input) {
  if (!input || typeof input !== 'string') return null;
  const s = input.trim();
  const m = s.match(
    /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}
